// imageCompress — client-side image compression helper (2026-05-20).
//
// Wraps `browser-image-compression` with platform-wide defaults +
// per-asset presets. Every file picked from a user-facing <input
// type="file"> goes through `compressForUpload` before being
// appended to FormData. The server-side sharp pipeline still runs
// (security baseline — metadata strip + polyglot defence), so this
// is purely a bandwidth/latency optimisation in front of it.
//
// Defense-in-depth contract:
//   • Client compression NEVER replaces server re-encode.
//   • If compression fails (browser bug, unusual format), we return
//     the original file unchanged — the server normalises it anyway.
//   • Output is always a File instance with the original filename
//     (extension may change to match the new mime) so existing
//     FormData.append('image', file) call sites continue to work.
//
// Behaviour:
//   • Files under `skipBelowBytes` (default 200 KB) pass through
//     untouched — compressing already-small files often INCREASES
//     bytes because of the new JPEG/PNG headers + chroma re-encode.
//   • For PNG (transparency-required assets like offer banners) we
//     pass `fileType: 'image/png'` to avoid losing alpha.
//   • Uses a Web Worker by default (lib's default) so the UI stays
//     responsive on large iPhone screenshots.

import imageCompression from 'browser-image-compression';

export type CompressPreset =
  | 'avatar'         // 800px, 0.3 MB — admin/agent/player/bot avatars
  | 'payment_qr'     // 1024px, 0.5 MB — UPI QR codes
  | 'offer_banner'   // 1280px, 1.0 MB, PNG — transparency required
  | 'payment_proof'  // 1920px, 0.8 MB — screenshots of UPI transfers
  | 'app_icon'       // 1024px, 0.5 MB — admin app-icon override
  | 'referral_card'  // 512px, 0.3 MB, PNG — referral card icon (transparent)
  | 'generic';       // 1920px, 1.0 MB — fallback

type Preset = {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  /** Force a specific output mime (preserves alpha for PNG). */
  fileType?: string;
};

const PRESETS: Record<CompressPreset, Preset> = {
  avatar:        { maxSizeMB: 0.3, maxWidthOrHeight: 800 },
  payment_qr:    { maxSizeMB: 0.5, maxWidthOrHeight: 1024 },
  offer_banner:  { maxSizeMB: 1.0, maxWidthOrHeight: 1280, fileType: 'image/png' },
  payment_proof: { maxSizeMB: 0.8, maxWidthOrHeight: 1920 },
  app_icon:      { maxSizeMB: 0.5, maxWidthOrHeight: 1024 },
  referral_card: { maxSizeMB: 0.3, maxWidthOrHeight: 512, fileType: 'image/png' },
  generic:       { maxSizeMB: 1.0, maxWidthOrHeight: 1920 },
};

/** Bytes; files smaller than this skip compression entirely. */
const SKIP_BELOW_BYTES = 200 * 1024;

export type CompressOptions = {
  preset?: CompressPreset;
  /** Override the preset's max size. */
  maxSizeMB?: number;
  /** Override the preset's max width/height. */
  maxWidthOrHeight?: number;
  /** Force a specific output mime. */
  fileType?: string;
  /** Bytes; files smaller than this skip compression. */
  skipBelowBytes?: number;
};

/**
 * Compress an uploaded image for transit. Returns a new File ready
 * for FormData. Falls back to the original on any error or when the
 * source is already small enough.
 */
export async function compressForUpload(
  file: File,
  opts: CompressOptions = {},
): Promise<File> {
  if (!file || !(file instanceof File)) return file;
  const skipBelow = opts.skipBelowBytes ?? SKIP_BELOW_BYTES;
  if (file.size <= skipBelow) return file;

  const preset = PRESETS[opts.preset ?? 'generic'];
  const settings = {
    maxSizeMB:        opts.maxSizeMB ?? preset.maxSizeMB,
    maxWidthOrHeight: opts.maxWidthOrHeight ?? preset.maxWidthOrHeight,
    useWebWorker:     true,
    initialQuality:   0.82,
    // Preserve the original mime for transparency-bearing inputs.
    // Without this, the lib defaults to JPEG which would silently
    // flatten alpha to black/white on offer banners.
    fileType:         opts.fileType ?? preset.fileType,
  };

  try {
    // Typed as Blob (the lib's declared return is File, but at runtime it can
    // return a plain Blob) so the `instanceof File` guard + Blob fall-through
    // below stay reachable to the type-checker.
    const out: Blob = await imageCompression(file, settings);
    // If compression backfired (output bigger than input — rare but
    // possible on already-optimised sources), fall back to original.
    if (out.size >= file.size) return file;
    // Promote Blob back to File so downstream FormData.append keeps
    // the filename. The lib does this on most paths but guarding is
    // cheap.
    if (out instanceof File) return out;
    return new File([out], file.name, { type: out.type || file.type });
  } catch (e) {
    // Defense-in-depth: never block an upload because compression
    // failed — the server still strips + normalises.
    // eslint-disable-next-line no-console
    console.warn('[imageCompress] fell back to original:', (e as Error)?.message);
    return file;
  }
}
